import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import CashBookPrint from "./CashBookPrint";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";
import price from "../../helpers/calculatePrice";
import roundOffTotal from "../../helpers/roundOffTotal";
import moment from "moment";

/**
 * Build a flat ESC/POS receipt matching the PrintComponent/BillComponent.js layout exactly:
 * Header (restaurant, address, FSSAI) → customer name → branchOrderNumber + date
 * → orderType + orderNumber + table + payment → Item/Qty/Price/Amt table
 * → Sub Total, Discount, GST, Table Price, Charges, Round Off, Grand Total
 * → GST number (Tax Summary) → receipt message
 *
 * jobId format: "bill:<branchOrderNumber>" — stable per billing event.
 */
function createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, tableType, printSetting, W }) {
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `bill:${branchOrderNumber}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const isExclusiveGST = printSetting?.exclusiveGST ?? false;
  const roundOffType = printSetting?.lowerRoundOff
    ? "lowerRoundOff"
    : printSetting?.upperRoundOff
      ? "upperRoundOff"
      : "standardRoundOff";

  // Calculate totals (same as ProductTable in BillComponent)
  const subTotal = parseFloat(printData?.itemsTotal || 0);
  const discount = parseFloat(printData?.discount || 0);
  const otherCharges = parseFloat(printData?.otherCharges || 0);
  const tablePrice = parseFloat(printData?.tablePrice || 0);
  const gstCharges = isExclusiveGST
    ? parseFloat(printData?.cgstCharges || 0) + parseFloat(printData?.sgstCharges || 0)
    : 0;
  const unrounded = subTotal - discount + otherCharges + tablePrice + gstCharges;
  const grandTotal = roundOffTotal(unrounded, roundOffType);
  const roundOff = grandTotal - unrounded;

  const lines = [];

  const SEP = "-".repeat(W);
  const padR = (s, n) => { const str = String(s ?? ""); return str.length >= n ? str.slice(0, n) : str + " ".repeat(n - str.length); };
  const padL = (s, n) => { const str = String(s ?? ""); return str.length >= n ? str : " ".repeat(n - str.length) + str; };
  const spreadEven = (...parts) => {
    const p = parts.filter(Boolean);
    if (!p.length) return "";
    if (p.length === 1) return p[0];
    const tl = p.reduce((s, x) => s + x.length, 0);
    const sp = W - tl;
    const gaps = p.length - 1;
    const spc = sp > 0 ? Math.floor(sp / gaps) : 2;
    const extra = sp > 0 ? sp - spc * gaps : 0;
    return p.map((x, i) => i < p.length - 1 ? x + " ".repeat(spc + (i < extra ? 1 : 0)) : x).join("");
  };
  // ── Column layout strategy ────────────────────────────────────────────────
  // 58mm: 2 columns via native bridge — Item (left) | Qty + Amt (right, 11 chars)
  //        Price is dropped because the bridge only allocates ~12 chars right.
  // 80mm: 4 fixed-position columns via `text` lines with manual padding:
  //        Name(20) + Qty(4) + Price(9) + Amt(9) = 42 chars exactly.
  //        Using `text` instead of `columns` guarantees fixed column positions
  //        regardless of item name length.

  // Visual width: Gujarati/Devanagari chars print ~2x wider on thermal printers.
  const visualWidth = (str) =>
    [...String(str ?? "")].reduce((sum, ch) => sum + (ch.charCodeAt(0) > 127 ? 2 : 1), 0);

  // 80mm column widths (must sum to W=42)
  //   C_NAME(24) + C_QTY(4) + C_PRICE(7) + C_AMT(7) = 42
  //   Using tighter widths to minimise the gap between Price and Amt values:
  //   padL(180, 7) = "    180", padL(180.00, 7) = " 180.00" → only 1 space gap.
  const C_NAME  = 24; // item name visual column (wider = longer names without wrapping)
  const C_QTY   =  4; // quantity
  const C_PRICE =  7; // unit price  (fits up to "9999.99")
  const C_AMT   =  7; // line total  (fits up to "9999.99")

  // Build a single fixed-position text line for 80mm
  const build80Line = (name, qtyVal, priceVal, amtVal) => {
    const nameLines = [];
    let currentLine = "";
    let currentWidth = 0;
    const chars = [...String(name ?? "")];
    
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i];
      const charWidth = ch.charCodeAt(0) > 127 ? 2 : 1;
      
      if (currentWidth + charWidth > C_NAME) {
        nameLines.push(currentLine);
        currentLine = ch;
        currentWidth = charWidth;
      } else {
        currentLine += ch;
        currentWidth += charWidth;
      }
    }
    if (currentLine) {
      nameLines.push(currentLine);
    }
    if (nameLines.length === 0) nameLines.push("");

    const result = [];
    const firstLineName = nameLines[0];
    const vw = visualWidth(firstLineName);
    const gap = " ".repeat(C_NAME - vw);
    
    result.push({
      text: firstLineName + gap + padL(qtyVal, C_QTY) + padL(priceVal, C_PRICE) + padL(amtVal, C_AMT),
      align: "left"
    });
    
    for (let i = 1; i < nameLines.length; i++) {
      result.push({ text: nameLines[i], align: "left" });
    }
    
    return result;
  };

  // 58mm right-column string (Qty + Amt only, 11 chars)
  const rightStr58 = (qty, amt) => `${padL(qty, 3)}${padL(amt, 8)}`;

  // ── RESET: force printer to left-align before every job ───────────────────
  lines.push({ text: "", align: "left" });

  // ── Header: restaurant | address | FSSAI, then blank line (no ---)  ───────
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  if (fssaiNumber) lines.push({ text: `FSSAI Number ${fssaiNumber}`, align: "center" });
  lines.push({ text: "", align: "left" });

  // ── Info rows ─────────────────────────────────────────────────────────────
  const { branchOrderNumber: bon, orderNumber, customerName, customerMobile, orderType, tableNumber, updatedAt } = printData;

  if (printSetting?.enableCustomer && customerName) {
    lines.push({ text: `Name : ${customerName}${customerMobile ? ` (${customerMobile})` : ""}`, align: "left" });
  }

  const dateStr = updatedAt ? moment(updatedAt).format("DD/MM/YYYY HH:mm") : moment().format("DD/MM/YYYY HH:mm");
  lines.push({ columns: [bon ?? "", dateStr] });

  const metaLine = [orderType, orderNumber ? String(orderNumber) : "", tableNumber ? String(tableNumber) : ""].filter(Boolean);
  if (metaLine.length > 0) {
    if (metaLine.length === 1) lines.push({ text: metaLine[0] });
    else if (metaLine.length === 2) lines.push({ columns: [metaLine[0], metaLine[1]] });
    else lines.push({ columns: [metaLine[0], `${metaLine[1]}    ${metaLine[2]}`] });
  }

  // Separator before item table
  lines.push({ text: SEP, align: "center" });

  // ── Item table header ─────────────────────────────────────────────────────
  if (W === 42) {
    // 80mm: fixed-position 4-column header as a single text line
    const hdrLine = padR("Item", C_NAME) + padL("Qty", C_QTY) + padL("Price", C_PRICE) + padL("Amt", C_AMT);
    lines.push({ text: hdrLine, align: "left", bold: true });
  } else {
    // 58mm: native 2-column bridge (Item | Qty Amt)
    lines.push({ columns: ["Item", `${padL("Qty", 3)}${padL("Amt", 8)}`], bold: true });
  }
  lines.push({ text: SEP, align: "center" });

  // ── Item rows ─────────────────────────────────────────────────────────────
  (printData?.orderItems ?? [])
    .filter((item) => item.quantity > 0)
    .forEach((item) => {
      const unitPrice = price(tableType?.tableType, item) || item.itemPrice || 0;
      const lineTotal = (item.quantity * unitPrice).toFixed(2);
      const name = item.itemName ?? "";

      if (W === 42) {
        // 80mm: fixed 4-column text lines
        build80Line(name, item.quantity, unitPrice, lineTotal).forEach(l => lines.push(l));
      } else {
        // 58mm: native 2-column bridge
        const maxName = 20;
        if (visualWidth(name) > maxName) {
          lines.push({ text: name, align: "left" });
          lines.push({ columns: ["", rightStr58(item.quantity, lineTotal)] });
        } else {
          lines.push({ columns: [name, rightStr58(item.quantity, lineTotal)] });
        }
      }
    });

  // ── Totals — right-aligned (left side empty, matches image) ──────────────
  const showSubTotal = discount !== 0 || gstCharges !== 0 || otherCharges !== 0 || tablePrice !== 0;
  if (showSubTotal) {
    lines.push({ columns: ["", `Sub Total  \u20B9 ${subTotal.toFixed(2)}`] });
  }
  if (discount !== 0) {
    lines.push({ columns: ["", `Discount(-)  \u20B9 ${discount.toFixed(2)}`] });
  }
  if (isExclusiveGST && gstCharges !== 0) {
    lines.push({ columns: ["", `GST(+)  \u20B9 ${gstCharges.toFixed(2)}`] });
  }
  if (tablePrice !== 0) {
    lines.push({ columns: ["", `Table Price(+)  \u20B9 ${tablePrice.toFixed(2)}`] });
  }
  if (otherCharges !== 0) {
    lines.push({ columns: ["", `Charges(+)  \u20B9 ${otherCharges.toFixed(2)}`] });
  }
  const roundOffDisplay = parseFloat(roundOff.toFixed(2));
  if (roundOffDisplay !== 0) {
    lines.push({ columns: ["", `Round off  ${roundOffDisplay > 0 ? "+" : ""}${roundOffDisplay.toFixed(2)}`] });
  }
  // Grand Total — always, right-aligned, bold
  lines.push({ columns: ["", `Grand Total  \u20B9 ${grandTotal.toFixed(2)}`], bold: true });

  // Tax Summary
  if (gstNumber) {
    lines.push({ text: SEP, align: "center" });
    lines.push({ text: "" });
    lines.push({ text: "Tax Summary", align: "center", bold: true });
    lines.push({ text: `GST ${gstNumber}`, align: "center" });
    lines.push({ text: "" });

    // Compute taxable amount and GST rates for the summary table
    const cgstAmt = parseFloat(printData?.cgstCharges || 0);
    const sgstAmt = parseFloat(printData?.sgstCharges || 0);
    const taxableAmt = subTotal - discount; // taxable base
    const cgstPct = taxableAmt > 0 ? ((cgstAmt / taxableAmt) * 100).toFixed(1) : "0";
    const sgstPct = taxableAmt > 0 ? ((sgstAmt / taxableAmt) * 100).toFixed(1) : "0";

    // Header row: Taxable Amt | CGST@X% | SGST@X%
    // Bridge supports 2 cols — combine CGST+SGST on right
    lines.push({ columns: ["Taxable Amt", `CGST@${cgstPct}%  SGST@${sgstPct}%`], bold: true });
    lines.push({ text: SEP, align: "center" });
    lines.push({ columns: [taxableAmt.toFixed(2), `${cgstAmt.toFixed(2)}         ${sgstAmt.toFixed(2)}`] });
  }

  // ── Receipt message footer — bold, center ────────────────────────────────
  if (receiptMessage) {
    lines.push({ text: "", align: "left" });
    lines.push({ text: receiptMessage, align: "center", bold: true });
  }

  // ── 2 inch paper feed (≈ 16 blank lines) so the last line is easy to tear ──
  for (let i = 0; i < 16; i++) lines.push({ text: "", align: "left" });

  return { jobId, printer: "bill", lines };
}


const PrintComponent = ({
  headers,
  footers,
  printData,
  printType,
  restaurant,
  logo,
  gstNumber,
  fssaiNumber,
  branchAddress,
  containerStyle,
  receiptMessage,
  printSetting,
  isRestaurantAdmin,
  tableType,
}) => {
  const isPrinting = useDetectPrint();

  const componentRef = useRef();
  const lastPrintData = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isRestaurantAdmin) {
      handlePrint();
      return;
    }

    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid() && printType !== "cashbook") {
      try {
        if (lastPrintData.current === printData) return; // Deduplicate Strict Mode double renders
        lastPrintData.current = printData;

        androidPrinter.getProfiles().then(result => {
          const profile = result.profiles?.bill;
          const W = (profile?.width === 576 || profile?.width === 832) ? 42 : 32;

          const receipt = createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, tableType, printSetting, W });
          receipt.jobId = `${receipt.jobId}:${Date.now()}`;

          return androidPrinter.print(receipt).then(
            (result) => console.log("Android bill print:", result.status, receipt.jobId),
            (error) => {
              console.error("Android bill print failed:", error.message, error.result);
              window.alert(`Print failed: ${error.message}`);
            }
          );
        }).catch(error => {
          console.error("Android bill receipt build error:", error.message);
          window.alert(`Print error: ${error.message}`);
        });
      } catch (error) {
        console.error("Android bill receipt build error:", error.message);
        window.alert(`Print error: ${error.message}`);
      }
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  const renderComponent = () => {
    console.log(
      "cash book",
      printData,
      restaurant,
      logo,
      gstNumber,
      receiptMessage,
      branchAddress,
      printSetting,
    );
    if (printType === "cashbook") {
      return (
        <CashBookPrint
          ref={componentRef}
          printData={printData}
          restaurant={restaurant}
          logo={logo}
          gstNumber={gstNumber}
          fssaiNumber={fssaiNumber}
          receiptMessage={receiptMessage}
          branchAddress={branchAddress}
          printSetting={printSetting}
        />
      );
    }
    return (
      <BillComponent
        ref={componentRef}
        orderData={printData}
        restaurant={restaurant}
        logo={logo}
        gstNumber={gstNumber}
        fssaiNumber={fssaiNumber}
        receiptMessage={receiptMessage}
        branchAddress={branchAddress}
        printSetting={printSetting}
        tableType={tableType}
      />
    );
  };
  return (
    <div style={{ ...containerStyle, width: "100%" }}>
      <div style={{ display: "none" }}>{renderComponent()}</div>
    </div>
  );
};
export default PrintComponent;
